import type { LogLine, LogStreamEndReason, LogStreamHandle, LogStreamStatus } from "./client";

/** The producer-side surface used by an `ApiClient` adapter to drive a
 * `LogStreamHandle` — `realClient.ts`'s EventSource wiring and
 * `mockClient.ts`'s timer both call these `emit*` methods; only the
 * subscribe/`close()` half (`LogStreamHandle`) is visible to callers like
 * `ContainerDetailPage`. */
interface LogStreamEmitter extends LogStreamHandle {
  emitLine(line: LogLine): void;
  emitStatus(status: LogStreamStatus): void;
  /** Also tears the stream down (calls the `teardown` passed to the
   * factory) — the server's terminal `end` event means the connection is
   * already over, so there's nothing left to subscribe to afterward. */
  emitEnd(reason: LogStreamEndReason): void;
}

/**
 * Minimal pub-sub shared by both `LogStreamHandle` adapters so the
 * subscribe/unsubscribe bookkeeping and the "already closed, ignore
 * anything further" guard live in exactly one place rather than being
 * duplicated between the real (EventSource) and mock (timer) transports.
 *
 * `teardown` is the transport-specific cleanup (`EventSource.close()` /
 * `clearInterval`) — guaranteed to run at most once, whether triggered by an
 * explicit `close()` call or internally after `emitEnd`.
 */
export function createLogStreamEmitter(teardown: () => void): LogStreamEmitter {
  const lineHandlers = new Set<(line: LogLine) => void>();
  const statusHandlers = new Set<(status: LogStreamStatus) => void>();
  const endHandlers = new Set<(reason: LogStreamEndReason) => void>();
  let closed = false;

  const handle: LogStreamEmitter = {
    onLine(handler) {
      lineHandlers.add(handler);
      return () => lineHandlers.delete(handler);
    },
    onStatus(handler) {
      statusHandlers.add(handler);
      return () => statusHandlers.delete(handler);
    },
    onEnd(handler) {
      endHandlers.add(handler);
      return () => endHandlers.delete(handler);
    },
    close() {
      if (closed) return;
      closed = true;
      lineHandlers.clear();
      statusHandlers.clear();
      endHandlers.clear();
      teardown();
    },
    emitLine(line) {
      if (closed) return;
      for (const handler of lineHandlers) handler(line);
    },
    emitStatus(status) {
      if (closed) return;
      for (const handler of statusHandlers) handler(status);
    },
    emitEnd(reason) {
      if (closed) return;
      for (const handler of endHandlers) handler(reason);
      handle.close();
    },
  };

  return handle;
}
